import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Builds trusted HTML for a release-note video field.
 * Accepts raw HTML, a YouTube embed URL, or a media file path.
 * Never mutates the source note — call sites should assign the result on a clone.
 */
export function toReleaseNoteVideoHtml(
    sanitizer: DomSanitizer,
    video: unknown,
    iframeTitle = 'Video',
): SafeHtml | null {
    if (video == null || video === '') {
        return null;
    }

    // Already trusted from a previous pass — do not wrap again (toString() leaks the SafeValue warning).
    if (typeof video !== 'string') {
        return video as SafeHtml;
    }

    return sanitizer.bypassSecurityTrustHtml(buildReleaseNoteVideoMarkup(video.trim(), iframeTitle));
}

function buildReleaseNoteVideoMarkup(video: string, iframeTitle: string): string {
    if (video.startsWith('<')) {
        return video;
    }

    if (isYoutubeEmbedUrl(video)) {
        const safeTitle = escapeHtmlAttribute(iframeTitle);
        return `<iframe src="${video}" title="${safeTitle}" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy" style="width:100%;aspect-ratio:16/9;border:0;border-radius:8px;"></iframe>`;
    }

    return `<video src="${video}" controls playsinline preload="metadata" style="width:100%;border-radius:8px;background:#000;"></video>`;
}

function isYoutubeEmbedUrl(value: string): boolean {
    return value.includes('youtube.com/embed/') || value.includes('youtube-nocookie.com/embed/');
}

function escapeHtmlAttribute(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
